// =============================================================================
// PEDRA — src/components/StatsPanel.tsx
// Corpos dos cartoes de estatistica da coluna da direita.
//
// Cada componente daqui e' o CONTEUDO de um cartao — o cartao em si (titulo,
// botao de recolher, moldura) mora em ResultsPanel. Todos foram desenhados
// para uma coluna estreita (~340 px): tabelas densas de duas colunas
// rotulo/valor, como no CutList Optimizer original.
//
// Toda a formatacao numerica passa por core/units (pt-BR).
// =============================================================================

import type { ReactElement, ReactNode } from 'react';
import type {
  EdgeBandType,
  Id,
  Micron,
  OptimizationResult,
  Settings,
  SheetLayout,
} from '../core/types';
import {
  formatArea,
  formatBigLength,
  formatDimensions,
  formatLength,
  formatNumber,
  formatPercent,
} from '../core/units';
import { formatCount, priorityLabel } from '../lib/format';

// -----------------------------------------------------------------------------
// PECAS BASICAS
// -----------------------------------------------------------------------------

/** Uma linha "rotulo | valor" da tabela densa. */
function Row(props: { label: string; value: ReactNode; sub?: string }): ReactElement {
  return (
    <tr className="stats-kv-row">
      <th scope="row" className="stats-kv-label">
        {props.label}
      </th>
      <td className="stats-kv-value tabular">
        {props.value}
        {props.sub ? <span className="stats-kv-sub">{props.sub}</span> : null}
      </td>
    </tr>
  );
}

/** Barra empilhada: usado · sobra util · desperdicio. */
function UsageBar(props: { used: number; reusable: number; waste: number; title: string }): ReactElement {
  const total = Math.max(1, props.used + props.reusable + props.waste);
  const pct = (v: number): string => `${(v / total) * 100}%`;
  return (
    <div className="stats-bar" title={props.title} role="img" aria-label={props.title}>
      <div className="stats-bar-seg is-used" style={{ width: pct(props.used) }} />
      <div className="stats-bar-seg is-reusable" style={{ width: pct(props.reusable) }} />
      <div className="stats-bar-seg is-waste" style={{ width: pct(props.waste) }} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// CALCULOS COMPARTILHADOS
// -----------------------------------------------------------------------------

interface OffcutTotals {
  usableArea: number;
  usableCount: number;
  scrapArea: number;
  total: number;
}

/** Separa, nas chapas dadas, o que ainda da para reaproveitar do refugo puro. */
function offcutTotals(sheets: readonly SheetLayout[]): OffcutTotals {
  let usableArea = 0;
  let usableCount = 0;
  let scrapArea = 0;
  let total = 0;
  for (const s of sheets) {
    for (const o of s.offcuts) {
      total += s.repeat;
      if (o.usable) {
        usableArea += o.area * s.repeat;
        usableCount += s.repeat;
      } else {
        scrapArea += o.area * s.repeat;
      }
    }
  }
  return { usableArea, usableCount, scrapArea, total };
}

interface BandRow {
  id: string;
  name: string;
  color: string;
  total: Micron;
}

function bandRowsOf(
  totals: Record<Id, Micron>,
  edgeBands: readonly EdgeBandType[],
): BandRow[] {
  const rows: BandRow[] = [];
  for (const [id, total] of Object.entries(totals)) {
    if (!total) continue;
    const band = edgeBands.find((b) => b.id === id);
    rows.push({
      id,
      name: band ? band.name || 'Fita sem nome' : 'Fita removida do cadastro',
      color: band ? band.color : '#8C8C8C',
      total,
    });
  }
  rows.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'pt-BR'));
  return rows;
}

/** ResultsPanel usa para decidir se vale a pena mostrar o cartao de fitas. */
export function hasEdgeBandTotals(result: OptimizationResult): boolean {
  return Object.values(result.stats.edgeBandingTotals).some((v) => Boolean(v));
}

// -----------------------------------------------------------------------------
// 1. ESTATISTICAS (RESULTADO INTEIRO)
// -----------------------------------------------------------------------------

export interface StatsPanelProps {
  result: OptimizationResult;
  settings: Settings;
}

export function StatsPanel(props: StatsPanelProps): ReactElement {
  const { result, settings } = props;
  const { stats, sheets } = result;
  const unit = settings.unit;
  const dp = settings.decimalPlaces;

  const off = offcutTotals(sheets);
  const scrap = Math.max(0, stats.totalWasteArea - off.usableArea);

  const elapsedText =
    stats.elapsedMs < 1000
      ? `${formatNumber(stats.elapsedMs, 0)} ms`
      : `${formatNumber(stats.elapsedMs / 1000, 2)} s`;

  return (
    <div className="stats-root">
      <UsageBar
        used={stats.totalUsedArea}
        reusable={off.usableArea}
        waste={scrap}
        title={`Utilizado ${formatPercent(stats.usedAreaRatio)}, sobras reaproveitáveis ${formatArea(
          off.usableArea,
          unit,
        )}, desperdício ${formatArea(scrap, unit)}`}
      />

      <table className="stats-kv">
        <tbody>
          <Row
            label="Chapas utilizadas"
            value={formatCount(stats.numSheets)}
            sub={
              sheets.length !== stats.numSheets
                ? `${formatCount(sheets.length)} plano(s) de corte`
                : undefined
            }
          />
          <Row
            label="Área total utilizada"
            value={formatArea(stats.totalUsedArea, unit)}
            sub={formatPercent(stats.usedAreaRatio)}
          />
          <Row
            label="Área total desperdiçada"
            value={formatArea(stats.totalWasteArea, unit)}
            sub={formatPercent(stats.wasteRatio)}
          />
          <Row label="Área bruta das chapas" value={formatArea(stats.totalSheetArea, unit)} />
          <Row label="Cortes totais" value={formatCount(stats.numCuts)} />
          <Row
            label="Comprimento de corte total"
            value={formatBigLength(stats.totalCutLength, unit)}
          />
          <Row label="Espessura de corte" value={formatLength(settings.kerf, unit, dp)} />
          <Row
            label="Peças posicionadas"
            value={formatCount(stats.numPlacedParts)}
            sub={
              stats.numUnfitParts > 0
                ? `${formatCount(stats.numUnfitParts)} sem lugar`
                : undefined
            }
          />
          <Row
            label="Sobras reaproveitáveis"
            value={formatCount(off.usableCount)}
            sub={formatArea(off.usableArea, unit)}
          />
          <Row label="Prioridade de otimização" value={priorityLabel(settings.priority)} />
          <Row label="Tempo de cálculo" value={elapsedText} />
        </tbody>
      </table>

      <ul className="stats-legend">
        <li>
          <span className="stats-swatch is-used" /> Peças
        </li>
        <li>
          <span className="stats-swatch is-reusable" /> Sobras
        </li>
        <li>
          <span className="stats-swatch is-waste" /> Desperdício
        </li>
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 2. ESTATISTICAS DA CHAPA SELECIONADA
// -----------------------------------------------------------------------------

export interface SheetStatsPanelProps {
  sheet: SheetLayout | null;
  settings: Settings;
  /** Total de chapas do resultado, so para escrever "Chapa 2 de 7". */
  sheetCount: number;
}

export function SheetStatsPanel(props: SheetStatsPanelProps): ReactElement {
  const { sheet, settings, sheetCount } = props;
  const unit = settings.unit;
  const dp = settings.decimalPlaces;

  if (!sheet) {
    return <p className="stats-empty">Nenhuma chapa selecionada.</p>;
  }

  const off = offcutTotals([sheet]);
  const wasteRatio = 1 - sheet.usedAreaRatio;
  const scrap = Math.max(0, sheet.wasteArea - off.usableArea / Math.max(1, sheet.repeat));

  return (
    <div className="stats-root">
      <UsageBar
        used={sheet.usedArea}
        reusable={off.usableArea / Math.max(1, sheet.repeat)}
        waste={scrap}
        title={`Chapa ${sheet.index + 1}: ${formatPercent(sheet.usedAreaRatio)} de aproveitamento`}
      />

      <table className="stats-kv">
        <tbody>
          <Row
            label="Chapa"
            value={`${sheet.index + 1} de ${sheetCount}`}
            sub={sheet.label || 'Sem nome'}
          />
          <Row
            label="Dimensões"
            value={formatDimensions(sheet.length, sheet.width, unit, dp, settings.dimensionOrder)}
            sub={sheet.material || undefined}
          />
          {sheet.repeat > 1 ? (
            <Row label="Chapas iguais" value={`${formatCount(sheet.repeat)}×`} />
          ) : null}
          <Row
            label="Área utilizada"
            value={formatArea(sheet.usedArea, unit)}
            sub={formatPercent(sheet.usedAreaRatio)}
          />
          <Row
            label="Área desperdiçada"
            value={formatArea(sheet.wasteArea, unit)}
            sub={formatPercent(wasteRatio)}
          />
          <Row label="Peças" value={formatCount(sheet.placements.length)} />
          <Row
            label="Sobras"
            value={formatCount(sheet.offcuts.length)}
            sub={
              off.usableCount > 0
                ? `${formatCount(off.usableCount / Math.max(1, sheet.repeat))} reaproveitável(is)`
                : undefined
            }
          />
          <Row label="Cortes" value={formatCount(sheet.numCuts)} />
          <Row label="Comprimento de corte" value={formatBigLength(sheet.cutLength, unit)} />
          <Row label="Espessura de corte" value={formatLength(settings.kerf, unit, dp)} />
          <Row label="Prioridade de otimização" value={priorityLabel(settings.priority)} />
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 3. TABELA POR CHAPA
// -----------------------------------------------------------------------------

export interface SheetsTablePanelProps {
  result: OptimizationResult;
  settings: Settings;
  selectedIndex: number;
  onSelectSheet?(index: number): void;
}

export function SheetsTablePanel(props: SheetsTablePanelProps): ReactElement {
  const { result, settings, selectedIndex, onSelectSheet } = props;
  const unit = settings.unit;
  const dp = settings.decimalPlaces;

  return (
    <div className="stats-scroll">
      <table className="table table--zebra stats-table">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Chapa</th>
            <th scope="col" className="stats-num">
              Qtd
            </th>
            <th scope="col" className="stats-num">
              Peças
            </th>
            <th scope="col" className="stats-num">
              Aprov.
            </th>
            <th scope="col" className="stats-num">
              Cortes
            </th>
          </tr>
        </thead>
        <tbody>
          {result.sheets.map((s) => {
            const classes = ['stats-row'];
            if (onSelectSheet) classes.push('is-clickable');
            if (s.index === selectedIndex) classes.push('is-selected');
            return (
              <tr
                key={s.index}
                className={classes.join(' ')}
                onClick={onSelectSheet ? () => onSelectSheet(s.index) : undefined}
                title={
                  `${s.label || 'Sem nome'} — ${formatDimensions(
                    s.length,
                    s.width,
                    unit,
                    dp,
                    settings.dimensionOrder,
                  )}` +
                  (s.material ? ` — ${s.material}` : '') +
                  (onSelectSheet ? ' (clique para ver no diagrama)' : '')
                }
              >
                <td className="tabular">{s.index + 1}</td>
                <td className="truncate">{s.label || 'Sem nome'}</td>
                <td className="stats-num tabular">{s.repeat}</td>
                <td className="stats-num tabular">{formatCount(s.placements.length)}</td>
                <td className="stats-num tabular">{formatPercent(s.usedAreaRatio)}</td>
                <td className="stats-num tabular">{formatCount(s.numCuts)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// 4. SOBRAS
// -----------------------------------------------------------------------------

export interface OffcutsPanelProps {
  result: OptimizationResult;
  settings: Settings;
}

export function OffcutsPanel(props: OffcutsPanelProps): ReactElement {
  const { result, settings } = props;
  const unit = settings.unit;
  const dp = settings.decimalPlaces;
  const off = offcutTotals(result.sheets);

  return (
    <>
      <p className="stats-note">
        {`${formatCount(off.usableCount)} reaproveitável(is) · mínimo de ${formatLength(
          settings.minTrimDimension,
          unit,
          dp,
        )}`}
      </p>
      <div className="stats-scroll">
        <table className="table table--zebra stats-table">
          <thead>
            <tr>
              <th scope="col">Chapa</th>
              <th scope="col">Dimensões</th>
              <th scope="col" className="stats-num">
                Área
              </th>
              <th scope="col" className="stats-num">
                Qtd
              </th>
              <th scope="col">Situação</th>
            </tr>
          </thead>
          <tbody>
            {result.sheets.flatMap((s) =>
              s.offcuts.map((o) => (
                <tr key={`${s.index}-${o.id}`}>
                  <td className="tabular">{s.index + 1}</td>
                  <td className="tabular">
                    {formatDimensions(o.length, o.width, unit, dp, settings.dimensionOrder)}
                  </td>
                  <td className="stats-num tabular">{formatArea(o.area, unit)}</td>
                  <td className="stats-num tabular">{s.repeat}</td>
                  <td>
                    {o.usable ? (
                      <span className="chip stats-chip is-good">Reaproveitável</span>
                    ) : (
                      <span className="chip stats-chip">Refugo</span>
                    )}
                  </td>
                </tr>
              )),
            )}
            {result.sheets.every((s) => s.offcuts.length === 0) ? (
              <tr>
                <td colSpan={5} className="stats-empty">
                  Nenhuma sobra registrada.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// 5. FITA DE BORDA
// -----------------------------------------------------------------------------

export interface EdgeBandsPanelProps {
  result: OptimizationResult;
  settings: Settings;
  edgeBands: readonly EdgeBandType[];
}

export function EdgeBandsPanel(props: EdgeBandsPanelProps): ReactElement {
  const { result, settings, edgeBands } = props;
  const rows = bandRowsOf(result.stats.edgeBandingTotals, edgeBands);
  const unit = settings.unit;
  const dp = settings.decimalPlaces;

  if (rows.length === 0) {
    return <p className="stats-empty">Nenhuma fita de borda neste plano.</p>;
  }

  return (
    <div className="stats-scroll">
      <table className="table table--zebra stats-table">
        <thead>
          <tr>
            <th scope="col">Fita</th>
            <th scope="col" className="stats-num">
              Espessura
            </th>
            <th scope="col" className="stats-num">
              Metragem
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => {
            const band = edgeBands.find((x) => x.id === b.id);
            return (
              <tr key={b.id}>
                <td className="truncate">
                  <span className="stats-band-dot" style={{ background: b.color }} aria-hidden="true" />
                  {b.name}
                </td>
                <td className="stats-num tabular">
                  {band ? formatLength(band.thickness, unit, dp) : '—'}
                </td>
                <td className="stats-num tabular">{formatBigLength(b.total, unit)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default StatsPanel;
